import React, { useCallback } from "react"
import PropTypes from "prop-types"
import errorsPropType from "components/PropTypes/errorsPropType"
import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js"
import TextField from "components/common/TextField"
import { isEqual } from "underscore"
import FormFieldset from "components/FormFieldset"
import Analytics from "lib/Analytics"
import i18n from "lib/i18n"

// CB interchange fees range from 0.20% to 0.90%
// Visa interchange fees range from 1.15% + US$0.05 to 2.4% + US$0.10
// Mastercard interchange fees range from 1.15% + US$0.05 to 2.5% + US$0.10
//
// Therefore it makes the most sense to preference the network with the lowest
// interchange fees first, but we still must allow the user to select the
// card network for themselves.
//
// ref https://stripe.com/au/resources/more/credit-card-processing-fees-explained
// ref https://stripe.com/au/resources/more/cartes-bancaires-an-in-depth-guide
//
// These fees should be periodically reviewed and the `preferredNetwork` option updated
// as Stripe may enable other networks (like EFTPOS) which have lower fees than
// Visa and Mastercard.
//
const CARD_NETWORK_OPTIONS = {
  showIcon: true, // This enables the card network selector, or card type if no co-branded card
  preferredNetwork: ["cartes_bancaires", "visa", "mastercard"],
}

const CreditCardDetails = (props) => {
  const {
    errors = {
      cardNumber: "",
      cvv: "",
      month: "",
      year: "",
    },
    fieldsActive = false,
    onChange = () => {},
  } = props

  const track = useCallback(
    (label, propName) => {
      if (props[propName]) {
        Analytics.trackFormEvent("Donation Form", "Completed", label)
      } else {
        Analytics.trackFormEvent("Donation Form", "Skipped", label)
      }
    },
    [props],
  )

  const trackCardNumber = useCallback(() => track("Credit Card Number", "cardNumber"), [track])
  const trackExpiry = useCallback(() => track("Expiry", "expiry"), [track])
  const trackCVV = useCallback(() => track("CVV", "cvv"), [track])

  const handleChange = useCallback(
    (field) => (event) => onChange({ [field]: event.complete }),
    [onChange],
  )

  const setCardNumber = useCallback((event) => handleChange("cardNumber")(event), [handleChange])
  const setExpiry = useCallback((event) => handleChange("expiry")(event), [handleChange])
  const setCvv = useCallback((event) => handleChange("cvv")(event), [handleChange])

  return (
    <div id="credit-card">
      <FormFieldset>
        <div>
          <TextField
            disabled={!fieldsActive}
            error={!!errors.cardNumber}
            fullWidth
            helperText={errors.cardNumber}
            id="donation_cc_number"
            inputComponent={CardNumberElement}
            inputProps={{ options: CARD_NETWORK_OPTIONS }}
            label={i18n.t("donation.label.card_number")}
            onBlur={trackCardNumber}
            onChange={setCardNumber}
          />
        </div>
      </FormFieldset>

      <FormFieldset>
        <div className="flex flex-row items-start justify-between">
          <div className="mb-5 flex flex-row items-end">
            <TextField
              classes="w-23 mr-4"
              disabled={!fieldsActive}
              error={!!errors.expiry}
              helperText={errors.expiry}
              id="donation_cc_expiry"
              inputComponent={CardExpiryElement}
              label={i18n.t("donation.label.expiry")}
              onBlur={trackExpiry}
              onChange={setExpiry}
              placeholder={i18n.t("donation.placeholder.expiry")}
            />
          </div>

          <TextField
            classes="w-23 float-right"
            disabled={!fieldsActive}
            error={!!errors.cvv}
            helperText={errors.cvv}
            id="donation_cc_verification"
            inputComponent={CardCvcElement}
            label={i18n.t("donation.label.cvv")}
            onBlur={trackCVV}
            onChange={setCvv}
            placeholder=""
          />
        </div>
      </FormFieldset>
    </div>
  )
}

CreditCardDetails.propTypes = {
  errors: errorsPropType,
  fieldsActive: PropTypes.bool,
  onChange: PropTypes.func,

  // The fields below are not referenced by name (hence the eslint-disable), but are used by
  // track() via props[propName] to instrument the user's interaction with the form.
  // Stripe won't tell us these values, just whether they are complete.

  cardNumber: PropTypes.bool,
  cvv: PropTypes.bool,
  expiry: PropTypes.bool,
}

export default React.memo(CreditCardDetails, (prevProps, nextProps) =>
  isEqual(prevProps, nextProps),
)
